import React, { useState } from "react";
import "./OtpInput.css";

// Six boxes drawn over one transparent input. A single real field keeps SMS
// autofill (autocomplete="one-time-code"), paste, password managers and
// screen readers working the way they do on any text field; the boxes only
// draw its value.
export default function OtpInput({
  id,
  value,
  onChange,
  onComplete,
  length = 6,
  readOnly = false,
  invalid = false,
  autoFocus = false,
  describedBy,
}) {
  const [focused, setFocused] = useState(false);

  const commit = (digits) => {
    // A stray extra keystroke on a full code changes nothing and must not
    // verify the same code twice.
    if (digits === value) return;
    onChange(digits);
    if (digits.length === length && onComplete) onComplete(digits);
  };

  const handleChange = (e) => {
    let digits = e.target.value.replace(/\D/g, "");
    if (digits.length > length) {
      // A whole code landing at once (SMS autofill into a part-filled field)
      // replaces what was there; a stray extra keystroke is dropped.
      digits = digits.length - value.length >= length
        ? digits.slice(-length)
        : digits.slice(0, length);
    }
    commit(digits);
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
    e.preventDefault();
    if (pasted && !readOnly) commit(pasted.slice(0, length));
  };

  // Keep the caret at the end, whichever box was clicked, so typing fills the
  // next box and Backspace clears the last one.
  const pinCaret = (e) => {
    const field = e.currentTarget;
    const end = field.value.length;
    if (field.selectionStart !== end || field.selectionEnd !== end) {
      field.setSelectionRange(end, end);
    }
  };

  const activeIndex = Math.min(value.length, length - 1);

  return (
    <div className={`otp${invalid ? " is-invalid" : ""}${readOnly ? " is-busy" : ""}`}>
      <input
        id={id}
        className="otp-input"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        spellCheck={false}
        value={value}
        onChange={handleChange}
        onPaste={handlePaste}
        onFocus={(e) => { setFocused(true); pinCaret(e); }}
        onBlur={() => setFocused(false)}
        onSelect={pinCaret}
        readOnly={readOnly}
        autoFocus={autoFocus}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      />
      <div className="otp-slots" aria-hidden="true">
        {Array.from({ length }, (_, i) => {
          const digit = value[i] || "";
          const active = focused && !readOnly && i === activeIndex;
          return (
            <span
              key={i}
              className={`otp-slot${digit ? " is-filled" : ""}${active ? " is-active" : ""}`}
            >
              {digit}
              {active && !digit && <span className="otp-caret" />}
            </span>
          );
        })}
      </div>
    </div>
  );
}
